import {
    Button,
    Divider,
    Fade,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    SelectProps,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { DbMarkerCategory } from "../../../Core/Api/Types/db-marker-category";
import { assetService, cacheService, shareService } from "../../../Core/Api/services";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import {
    GetActiveTimeline,
    GetCreatingOrEditingTimelineEvent,
    GetFocusedTimelineClip,
    GetTimelineClips,
    GetTimelineEventBeingEdited,
    GetTimelineMarkers,
    SetCreatingOrEditingTimelineEvent,
    SetFocusedTimelineClip,
    SetTimelineClips,
    SetTimelineMarkers,
    TimelineType,
} from "../../../Redux/Reducers/redux-slice-timeline";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import InputTimecode from "../../../Utils/Timecode/Timecode";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import {
    GetFrameRate,
    GetIsFileTCDisplayed,
    GetMarkers,
    GetPlayingState,
    GetTimecodeIn,
    GoToFrame,
    PlayPause,
    SetMarkByCurrentFrame,
    SetMarkInByFrame,
    SetMarkOutByFrame,
    SetSeek,
} from "../../../Redux/Reducers/redux-slice-react-player";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    FileTimecodeToTimecode,
    FrameToTimecode,
    TimecodeToFileTimecode,
    TimecodeToFrame,
    TimecodeToSec,
} from "../../../Utils/Timecode/ReactPlayer";
import { DbMarkerSeverity } from "../../../Core/Api/Types/db-marker-severity";
import { DbMarkerLevel } from "../../../Core/Api/Types/db-marker-level";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { InputOutlined, KeyboardReturn, OutputOutlined, VisibilityOutlined } from "@mui/icons-material";
import StatusMarkerRender from "../../../Utils/Marker/marker";
import { CreateClipRequest } from "../../../Core/Api/Types/create-clip-request";
import { AddClipsRequest } from "../../../Core/Api/Types/add-clips-request";
import { Clip } from "../../../Core/Api/Types/clip";
import PanelHeader from "../PanelHeader/panel-header";
import { ClipCardProps, ConvertAssetClipToClipCard, ConvertShareClipToClipCard } from "../../Cards/ClipCard/clip-card";
import Panel from "../Panel/panel";
import { PreviewContext } from "../../../Pages/admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";
import { shareClipToDTO } from "../../../Pages/ViewLink/view-link";
import { AddMarkerByObjectIdRequest } from "../../../Core/Api/Types/add-marker-by-object-id-request";
import { DbAssetMarkerInfos } from "../../../Core/Api/Types/db-asset-marker-infos";
import { MapQCMarkerData } from "../../Cards/QCMarkerCard/qc-marker-card";
import { useTranslation } from "react-i18next";
import useEventListener from "../../../Utils/Hooks/useEventListener";
import { KeyboardKey } from "../../MediaPlayer/ReactPlayerWrapper/player-controls";
import { SetCurrentFrame } from "../../../Redux/Reducers/redux-slice-timetool";
import { edit } from "ace-builds";
import { UpdateClipByIdRequest } from "../../../Core/Api/Types/update-clip-by-id-request";

export interface MarkerPanelProps {
    parentId: number;
    shareKey?: string;
    editMode?: boolean;
    customerId?: number;
    context: PreviewContext;
    markIn?: string;
    markOut?: string;
    onClose?: () => void;
    dataToKeep?: any;
}

export interface FamilyMarkerDto {
    name: string;
    id: number;
}

export interface TypeMarkerDto {
    name: string;
    id: number;
    familyId: number;
}

export interface GetMarkersByObjectIdResponseDto {
    families: FamilyMarkerDto[];
    types: TypeMarkerDto[];
}

export const PanelTimeline = (props: MarkerPanelProps) => {
    const { t } = useTranslation();
    const { parentId, context, shareKey } = props;
    const platformMode = cacheService.GetPlatformMode();
    const dispatch = useDispatch();

    // Redux
    const timelineType = useAppSelector(GetActiveTimeline);
    const playing = useAppSelector(GetPlayingState);
    const timelineEventBeingEdited = useAppSelector(GetTimelineEventBeingEdited);

    function handleDrawerCancel() {
        props.onClose && props.onClose();
        dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: false }));
    }

    // if player has not been used yet, launch it.
    useEffect(() => {
        if (!playing) {
            dispatch(PlayPause());

            if (timelineEventBeingEdited) {
                dispatch(GoToFrame(timelineEventBeingEdited.timecodes.in.markFrame));
            }
        }
    }, [timelineEventBeingEdited]);

    const [keysPressed, setKeysPressed] = useState<any>({});

    const handleKeyUp = useCallback((e: KeyboardEvent) => {
        setKeysPressed((x) => ({ ...x, [e.key]: false }));
    }, []);

    const handleKeyPress = useCallback(
        (e: KeyboardEvent) => {
            const activeElement = document.activeElement;
            if (
                activeElement &&
                ((activeElement.tagName.toLowerCase() === "input" && activeElement.ariaLabel !== "time-indicator") ||
                    activeElement.tagName.toLowerCase() === "textarea")
            ) {
                return;
            }

            setKeysPressed({ ...keysPressed, [e.key]: true });

            switch (e.key) {
                case "I":
                    if (keysPressed["Shift"]) {
                        dispatch(SetMarkByCurrentFrame("In"));
                    }
                    break;
                case "O":
                    if (keysPressed["Shift"]) {
                        dispatch(SetMarkByCurrentFrame("Out"));
                        const clipNameInput = document.getElementById("clipName");
                        if (clipNameInput) {
                            setTimeout(() => {
                                clipNameInput.focus();
                            }, 0);
                        }
                    }
                    break;
            }
        },
        [keysPressed]
    );

    useEventListener("keydown", handleKeyPress);
    useEventListener("keyup", handleKeyUp);

    return (
        <Panel
            open={true}
            onClose={(e) => handleDrawerCancel()}
            header={
                <PanelHeader
                    title={
                        timelineEventBeingEdited
                            ? timelineType === TimelineType.Markers
                                ? t("button:editMarker")
                                : t("common:editClip")
                            : timelineType === TimelineType.Markers
                                ? t("button:createMarker")
                                : t("common:createClip")
                    }
                    onClose={() => {
                        handleDrawerCancel();
                    }}
                />
            }
        >
            <Stack gap={1}>
                <Typography variant="h6" color="text.tier">
                    {t("component:body.inAndOutTimecodes")}
                </Typography>
                <TimelineTimecodeControls timelineTimecode="In" />
                <TimelineTimecodeControls timelineTimecode="Out" />
            </Stack>

            <Divider sx={{ mt: 2, mb: 2, mr: -2, ml: -2 }} />

            {timelineType === TimelineType.Markers && <MarkerForm context={context} parentId={parentId} />}

            {timelineType === TimelineType.Clips && (
                <ClipForm
                    context={context}
                    parentId={parentId}
                    shareKey={shareKey}
                    clipBeingEdited={timelineEventBeingEdited}
                />
            )}
        </Panel>
    );
};

export const MarkerForm = (props: { parentId: number; context: PreviewContext }) => {
    const { t } = useTranslation();
    const { parentId, context } = props;
    const [category, setCategory] = useState<DbMarkerCategory>(DbMarkerCategory.Human);
    const [severity, setSeverity] = useState<DbMarkerSeverity>(DbMarkerSeverity.INFO);
    const [level, setLevel] = useState<DbMarkerLevel>(DbMarkerLevel.Medium);
    const [enums, setEnums] = useState<GetMarkersByObjectIdResponseDto>();
    const [types, setTypes] = useState<TypeMarkerDto[]>([]);
    const [familyId, setFamilyId] = useState<number>(0);
    const [typeId, setTypeId] = useState<number>(0);
    const [comment, setComment] = useState<string>("");

    const timelineMarkers = useAppSelector(GetTimelineMarkers);
    const { markIn, markOut } = useAppSelector(GetMarkers);
    const frameRate = useAppSelector(GetFrameRate);
    const customSnackbar = useCustomSnackbar();
    const dispatch = useDispatch();

    const selectSeverityParams: Partial<SelectProps<unknown>> = {
        renderValue: (value) => <StatusMarkerRender status={value as DbMarkerSeverity}></StatusMarkerRender>,
        onChange: (x) => {
            return setSeverity(DbMarkerSeverity[x.target.value as DbMarkerSeverity]);
        },
    };

    useEffect(() => {
        assetService.GetMarkerEnums().then((x) => {
            setEnums(x);
            if (x !== undefined) setFamilyId(x.families.find((y) => true)!.id!);
        });
    }, []);

    useEffect(() => {
        if (enums !== undefined) {
            let newTypes = enums?.types.filter((element, index, array) => element.familyId === familyId);
            setTypes(newTypes);
            setTypeId(newTypes.find((y) => true)?.id ?? 0);
        }
    }, [familyId]);

    const saveMarker = useCallback(() => {
        assetService
            .AddMarkerOnObjectId(
                new AddMarkerByObjectIdRequest({
                    objectId: parentId,
                    marker: new DbAssetMarkerInfos({
                        objectId: parentId,
                        comment,
                        typeId,
                        familyId,
                        level,
                        frameIn: markIn.markFrame,
                        frameOut: markOut.markFrame,
                        frameRate: frameRate,
                        category,
                        severity,
                    }),
                })
            )
            .then((data) => {
                if (data.success && data.marker) {
                    customSnackbar(t("alert:success.markerHasBeenCreated"), "success");
                    dispatch(SetTimelineMarkers([...timelineMarkers, ...MapQCMarkerData([data.marker], frameRate)]));
                    dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: false }));
                } else {
                    customSnackbar(t("alert:error.markerCreation"), "warning");
                }
            });
    }, [parentId, markIn, markOut, comment, typeId, familyId, level, category, severity]);

    return (
        <Stack flexGrow={1}>
            {context !== PreviewContext.DriveEnlarged && context !== PreviewContext.DriveFolderPreview && (
                <>
                    <Stack spacing={1.5} direction="row" sx={{ mb: 2 }}>
                        <TextField
                            sx={{ flex: 3 }}
                            select
                            label={t("common:category")}
                            value={category}
                            onChange={(x: React.ChangeEvent<HTMLInputElement>) => setCategory(DbMarkerCategory[x.target.value])}
                        >
                            {Object.keys(DbMarkerCategory).map((x) => (
                                <MenuItem key={x} value={DbMarkerCategory[x]}>
                                    {x}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            sx={{ flex: 2 }}
                            select
                            value={severity}
                            label={t("common:severity")}
                            SelectProps={selectSeverityParams}
                        >
                            {Object.keys(DbMarkerSeverity).map((x) => (
                                <MenuItem key={x} value={DbMarkerSeverity[x]}>
                                    {x}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            sx={{ flex: 2 }}
                            select
                            label={t("common:level")}
                            value={level}
                            onChange={(x: React.ChangeEvent<HTMLInputElement>) => setLevel(DbMarkerLevel[x.target.value])}
                        >
                            {Object.keys(DbMarkerLevel).map((x) => (
                                <MenuItem key={x} value={DbMarkerLevel[x]}>
                                    {x}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>

                    <Stack spacing={1} direction="column" sx={{ mb: 2 }}>
                        <InputLabel htmlFor="select-family" sx={{ display: "inline-flex", flex: 1 }}>
                            {t("common:family")}
                        </InputLabel>
                        <Select
                            labelId={"select-family"}
                            id={"select-family"}
                            sx={{ flex: 1 }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                            label={t("common:family")}
                            value={familyId}
                            onChange={(x: SelectChangeEvent<number>) => {
                                if (typeof x.target.value !== "string") setFamilyId(x.target.value);
                            }}
                        >
                            {enums?.families.map((x) => (
                                <MenuItem key={x.id} value={x.id}>
                                    {x.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Stack>

                    <Stack spacing={1} direction="column" sx={{ mb: 2 }}>
                        <InputLabel htmlFor="select-type" sx={{ display: "inline-flex", flex: 3 }}>
                            {t("common:type")}
                        </InputLabel>
                        <Select
                            sx={{ flex: 3 }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                            disabled={enums === undefined}
                            value={typeId}
                            label={t("common:typeLowerCase")}
                        >
                            {types.map((x: TypeMarkerDto) => (
                                <MenuItem key={x.id} value={x.id} onClick={(e) => setTypeId(x.id)}>
                                    {x.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Stack>
                </>
            )}

            <FormControl sx={{ mb: 2, width: "100%" }} size="small">
                <InputLabel shrink htmlFor="clipName" sx={{ display: "inline-flex" }}>
                    {t("common:comment")}
                </InputLabel>
                <OutlinedInput
                    multiline
                    minRows={3}
                    inputProps={{ maxLength: 255 }}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    id="clipName"
                />
            </FormControl>

            <Stack direction="column" justifyContent={"flex-end"} flex={1} spacing={2}>
                <Button color="primary" variant="contained" fullWidth onClick={() => saveMarker()}>
                    {t("common:save")}
                </Button>
            </Stack>
        </Stack>
    );
};

export const ClipForm = (props: {
    context: PreviewContext;
    clipBeingEdited: ClipCardProps | null;
    shareKey?: string;
    parentId?: number;
}) => {
    const { t } = useTranslation();
    const { shareKey, context, clipBeingEdited, parentId } = props;
    const { markIn, markOut } = useAppSelector(GetMarkers);
    const frameRate = useAppSelector(GetFrameRate);
    const customSnackbar = useCustomSnackbar();
    const dispatch = useAppDispatch();
    const editMode = useMemo(() => {
        return clipBeingEdited !== null;
    }, [clipBeingEdited]);

    const timelineClips = useAppSelector(GetTimelineClips);
    const [clipName, setClipName] = useState<string>(clipBeingEdited?.clipName ?? "");
    const [userName, setUserName] = useState<string>(clipBeingEdited?.createdBy ?? "");
    const [clipDescription, setClipDescription] = useState<string>(clipBeingEdited?.clipDescription ?? "");

    const saveClip = useCallback(() => {
        if (context !== PreviewContext.Share) {
            if (editMode && clipBeingEdited?.id) {
                assetService
                    .UpdateClipById(
                        new UpdateClipByIdRequest({
                            clipObjectId: clipBeingEdited.id,
                            clipName: clipName,
                            clipDescription: clipDescription,
                            frameIn: markIn.markFrame,
                            frameOut: markOut.markFrame,
                        })
                    )
                    .then((data) => {
                        if (data.success && data.clip) {
                            customSnackbar(t("alert:success.clipHasBeenEdited"), "success");
                            dispatch(
                                SetTimelineClips([
                                    ...timelineClips.filter((clip) => clip.id !== data.clip?.id),
                                    ConvertAssetClipToClipCard(data.clip, frameRate),
                                ])
                            );
                            dispatch(SetFocusedTimelineClip(ConvertAssetClipToClipCard(data.clip, frameRate)));
                            dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: false }));
                        } else {
                            customSnackbar(t("alert:error.clipCreation"), "warning");
                        }
                    });
            } else {
                assetService
                    .CreateClip(
                        new CreateClipRequest({
                            parentVideoAssetId: parentId,
                            clipName: clipName,
                            clipDescription: clipDescription,
                            frameIn: markIn.markFrame,
                            frameOut: markOut.markFrame,
                        })
                    )
                    .then((data) => {
                        if (data.success) {
                            customSnackbar(t("alert:success.chipHasBeenCreated"), "success");
                            dispatch(SetTimelineClips([...timelineClips, ConvertAssetClipToClipCard(data.clip, frameRate)]));
                            dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: false }));
                        } else {
                            customSnackbar(t("alert:error.clipCreation"), "warning");
                        }
                    });
            }
        } else if (context === PreviewContext.Share) {
            if (shareKey) {
                shareService
                    .AddClips(
                        new AddClipsRequest({
                            userName: userName,
                            shareKey: shareKey,
                            // frame should not be to 0 (to avoid BE issues on this call)
                            clips: [
                                new Clip({
                                    frameIn: markIn.markFrame,
                                    frameOut: markOut.markFrame,
                                    comment: clipName,
                                    userName,
                                    date: new Date(),
                                    id: -500,
                                }),
                            ],
                        })
                    )
                    .then((response) => {
                        if (response.success) {
                            if (response.clips && response.clips.length > 0) {
                                customSnackbar(t("alert:success.clipCreated"), "success");
                                dispatch(
                                    SetTimelineClips([...timelineClips, ConvertShareClipToClipCard(shareClipToDTO(response.clips!)[0])])
                                );
                                dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: false }));
                            }
                        } else customSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
                    });
            } else customSnackbar(t("alert:error.couldNotSaveClip"), "warning");
        } else console.error("context not handle yet");
    }, [props, editMode, clipName, clipDescription, userName, markIn, markOut]);

    const isValid = useMemo(() => {
        if (clipName === "") return false;
        if (context === PreviewContext.Share && userName === "") return false;
        return true;
    }, [clipDescription, userName, clipName]);

    return (
        <Stack flexGrow={1}>
            <Typography variant="h6" mb={2} color="text.tier">
                {t("component:body.configureYourClip")}
            </Typography>
            <FormControl required sx={{ mb: 2, width: "100%" }} size="small">
                <InputLabel shrink htmlFor="clipName" sx={{ display: "inline-flex" }}>
                    {t("field:label.clipName")}
                </InputLabel>
                <OutlinedInput
                    inputProps={{ maxLength: 255 }}
                    value={clipName}
                    onChange={(e) => setClipName(e.target.value)}
                    id="clipName"
                />
            </FormControl>
            {context === PreviewContext.Share && (
                <FormControl>
                    <InputLabel required shrink htmlFor="clipUserName" sx={{ display: "inline-flex" }}>
                        {t("field:label.username")}
                    </InputLabel>
                    <OutlinedInput
                        inputProps={{}}
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        id="clipUserName"
                    />
                </FormControl>
            )}
            {context !== PreviewContext.Share && (
                <FormControl sx={{ mb: 2, width: "100%" }} size="small">
                    <InputLabel shrink htmlFor="clipName" sx={{ display: "inline-flex" }}>
                        {t("common:comment")}
                    </InputLabel>
                    <TextField
                        multiline
                        minRows={3}
                        value={clipDescription}
                        onChange={(e) => setClipDescription(e.target.value)}
                        id="comment"
                        sx={{
                            height: "unset!important",
                            minHeight: "unset!important",
                        }}
                    />
                </FormControl>
            )}
            <Stack direction="column" justifyContent={"flex-end"} flex={1} spacing={2}>
                <Button color="primary" variant="contained" fullWidth onClick={() => saveClip()} disabled={!isValid}>
                    {t("common:save")}
                </Button>
            </Stack>
        </Stack>
    );
};

export const TimelineTimecodeControls = (props: { timelineTimecode: "In" | "Out" }) => {
    const { t } = useTranslation();
    const { timelineTimecode } = props;
    const { markIn, markOut } = useAppSelector(GetMarkers);
    const isPlaying = useAppSelector(GetPlayingState);
    const dispatch = useAppDispatch();
    const [hover, setHover] = useState(false);

    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const hideControls = () => {
        clearTimeout(timerRef.current!);
        timerRef.current = setTimeout(() => setHover(false));
    };
    const showControls = () => {
        clearTimeout(timerRef.current!);
        setHover(true);
    };

    const handleFastForward = (frameCount: number) => {
        if (isPlaying) dispatch(PlayPause());
        if (timelineTimecode === "In") {
            dispatch(SetMarkInByFrame(markIn.markFrame + frameCount));
            dispatch(GoToFrame(markIn.markFrame + frameCount));
        } else if (timelineTimecode === "Out") {
            dispatch(SetMarkOutByFrame(markOut.markFrame + frameCount));
            dispatch(GoToFrame(markOut.markFrame + frameCount));
        }
    };

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
            gap={0.5}
            px={0.5}
            py={0.5}
            sx={(t) => ({ border: `1px solid ${t.palette.divider}`, borderRadius: 1 })}
            onMouseEnter={(e) => showControls()}
            onMouseLeave={(e) => hideControls()}
        >
            <Stack direction="row" alignItems="center" gap={0.5} flexGrow={1}>
                <Tooltip
                    title={
                        <Stack direction="column" gap={1} alignItems={"center"} p={1}>
                            <Typography>{t("tooltip:title.setTimecode", { timelineTimecode: timelineTimecode })}</Typography>
                            <Stack direction="row" gap={0.5} alignItems={"center"}>
                                <KeyboardKey>{t("tooltip:title.shift", { timelineTimecode: "Out" })}</KeyboardKey>
                                <Typography variant="body2" color="text.tier">
                                    +
                                </Typography>
                                <KeyboardKey>{timelineTimecode === "In" ? "I" : "O"}</KeyboardKey>
                            </Stack>
                        </Stack>
                    }
                >
                    <IconButton color="primary" size="small" onClick={(e) => dispatch(SetMarkByCurrentFrame(timelineTimecode))}>
                        {timelineTimecode === "Out" ? <OutputOutlined /> : <InputOutlined />}
                    </IconButton>
                </Tooltip>
                <Stack direction="row" flexGrow={1} justifyContent={"space-between"} alignItems={"center"}>
                    <TimelineTimecodeInput timelineTimecode={timelineTimecode} />
                    <Fade in={hover}>
                        <Stack direction="row" alignItems={"center"} gap={0.5}>
                            <Button sx={{ width: 32 }} variant="text" onClick={(e) => handleFastForward(-1)}>
                                -1f
                            </Button>
                            <Button sx={{ width: 32 }} variant="text" onClick={(e) => handleFastForward(1)}>
                                +1f
                            </Button>
                        </Stack>
                    </Fade>
                    <IconButton
                        color="ghost"
                        size="small"
                        onClick={(e) => dispatch(GoToFrame(timelineTimecode === "In" ? markIn.markFrame : markOut.markFrame))}
                    >
                        <VisibilityOutlined />
                    </IconButton>
                </Stack>
            </Stack>
        </Stack>
    );
};

export const TimelineTimecodeInput = (props: { timelineTimecode: "In" | "Out" }) => {
    const { timelineTimecode } = props;
    const [inputFocused, setInputFocused] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const marker = useAppSelector(GetMarkers);
    const isFileTCDisplayed = useAppSelector(GetIsFileTCDisplayed);
    const timecodeIn = useAppSelector(GetTimecodeIn);
    //   const timecode =
    //     timelineTimecode === "In"
    //       ? isFileTCDisplayed
    //         ? marker.markIn.markFrame ?? marker.markIn.markTimecode
    //         : marker.markIn.markTimecode
    //       : isFileTCDisplayed
    //       ? marker.markOut.markTimecodeFile ?? marker.markOut.markTimecode
    //       : marker.markOut.markTimecode;
    const frameRate = useAppSelector(GetFrameRate);

    const timecode =
        timelineTimecode === "In"
            ? FrameToTimecode(marker.markIn.markFrame, frameRate)
            : FrameToTimecode(marker.markOut.markFrame, frameRate);

    const [timecodeEdit, setTimecodeEdit] = useState<string>(timecode);

    const timecodeFinal = useMemo(() => {
        return isFileTCDisplayed ? TimecodeToFileTimecode(timecodeIn, timecode, frameRate) : timecode;
    }, [isFileTCDisplayed, timecode]);

    useEffect(() => {
        setTimecodeEdit(timecodeFinal);
    }, [inputFocused]);

    const handleKeyPress = useCallback(
        (e) => {
            if (e.key === "Enter") {
                setInputFocused(false);
                const frameFinal = TimecodeToFrame(
                    isFileTCDisplayed ? FileTimecodeToTimecode(timecodeIn, e.target.value, frameRate) : e.target.value,
                    frameRate
                );

                if (timelineTimecode) {
                    if (timelineTimecode === "In") dispatch(SetMarkInByFrame(frameFinal));
                    if (timelineTimecode === "Out") dispatch(SetMarkOutByFrame(frameFinal));
                }

                dispatch(
                    SetSeek(
                        TimecodeToFrame(
                            isFileTCDisplayed ? FileTimecodeToTimecode(timecodeIn, e.target.value, frameRate) : e.target.value,
                            frameRate
                        )
                    )
                );
            }
        },
        [isFileTCDisplayed, timecodeIn, frameRate]
    );

    return (
        <>
            {inputFocused ? (
                <InputTimecode
                    frameRate={frameRate}
                    handleKeyDown={handleKeyPress}
                    value={timecodeEdit}
                    setValue={setTimecodeEdit}
                    onMouseLeave={(e) => setInputFocused(false)}
                    size="small"
                    endAdornment={<KeyboardReturn />}
                    sx={(theme) => ({ ...theme.typography.subtitle2, width: 130 })}
                />
            ) : (
                <Stack direction="row" alignItems={"center"} gap={0} onClick={(e) => setInputFocused(true)} p={1}>
                    <Typography variant="body1" component="span" color="text.primary" sx={{ width: 20, textAlign: "center" }}>
                        {timecodeFinal.slice(0, 2)}
                    </Typography>
                    <Typography variant="body1" component="span" color="text.tier">
                        :
                    </Typography>
                    <Typography variant="body1" component="span" color="text.primary" sx={{ width: 20, textAlign: "center" }}>
                        {timecodeFinal.slice(3, 5)}
                    </Typography>
                    <Typography variant="body1" component="span" color="text.tier">
                        :
                    </Typography>
                    <Typography variant="body1" component="span" color="text.primary" sx={{ width: 20, textAlign: "center" }}>
                        {timecodeFinal.slice(6, 8)}
                    </Typography>
                    <Typography variant="body1" component="span" color="text.tier">
                        :
                    </Typography>
                    <Typography variant="body1" component="span" color="text.tier" sx={{ width: 20, textAlign: "center" }}>
                        {timecodeFinal.slice(9, timecodeFinal.length)}
                    </Typography>
                </Stack>
            )}
        </>
    );
};

const exactFrames = (frames: number, frameRate: number, frameRateToDisplay: number): number => {
    if (frameRate !== frameRateToDisplay) return (frames / frameRate) * frameRateToDisplay;
    else return frames;
};
